import { useEffect, useRef, useState } from 'react';
import { Check, LogOut, QrCode, Smartphone } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { Cargando } from '../ui/EstadoDePantalla';
import { ErrorDelContrato } from './VincularTerminal';
import { MODULO_TURNO, contextoDeTerminal } from './enlaceDeTerminal';
import { leerErrorDelContrato } from './errorDelContrato';

/* =====================================================================
   «Entrar a una terminal» · DISEÑO-F1 §7 · contrato v2 §3.1
   =====================================================================
   La terminal enseña un QR que cambia cada minuto. La persona lo escanea
   con la cámara de su teléfono, el navegador abre `/T/<código>/<nonce>`,
   `enlaceDeTerminal.js` lo traduce a `?m=terminal-turno&codigo=…&n=…` y
   esta pantalla, al montarse, canjea el nonce: su turno queda abierto y
   la terminal lo dice en su pantalla.

   POR QUÉ ES UN MÓDULO APARTE Y EN CONSULTA. «Terminales de conteo» nace
   en ENCARGADO; quien escanea es el operador. Sin un módulo a su alcance
   el QR lo llevaría a una puerta cerrada.

   EL NONCE SE CANJEA UNA SOLA VEZ (contrato §3.1). El efecto se protege
   con una referencia: en modo estricto React monta dos veces, y una
   segunda llamada se comería el nonce y contestaría 404 sin motivo.
   ===================================================================== */

const PRIMARIO = 'min-h-11 w-full rounded-xl bg-[var(--color-marca)] px-3 text-sm font-semibold text-white disabled:opacity-50';
const SECUNDARIO = 'min-h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800 disabled:opacity-50';

const ERRORES_ABRIR = { 404: 'NONCE_INVALIDO', 403: 'SIN_PERMISO', 429: 'DEMASIADAS_PETICIONES' };

export default function EntrarATerminal({ contexto, irA }) {
  const ctx = { ...(contextoDeTerminal(MODULO_TURNO, typeof window !== 'undefined' ? window.location.search : '') || {}),
                ...(contexto || {}) };
  const codigo = ctx.codigo || null;
  const nonce = ctx.n || null;
  const conQr = !!(codigo && nonce);

  // 'abriendo' | 'abierto' | 'error' | 'cerrando' | 'cerrado'
  const [estado, setEstado] = useState(conQr ? 'abriendo' : 'sin-qr');
  const [turno, setTurno] = useState(null);
  const [error, setError] = useState(null);
  const [cierre, setCierre] = useState(null);
  const pedido = useRef(null);

  useEffect(() => {
    if (!conQr) return;
    const llave = `${codigo}/${nonce}`;
    if (pedido.current === llave) return;
    pedido.current = llave;
    let vivo = true;
    api.post('/terminal/v2/turno/abrir', { codigo_terminal: codigo, nonce })
      .then((d) => { if (vivo) { setTurno(d); setEstado('abierto'); } })
      .catch((e) => {
        if (!vivo) return;
        setError(leerErrorDelContrato(e, { porEstado: ERRORES_ABRIR }));
        setEstado('error');
      });
    return () => { vivo = false; };
  }, [conQr, codigo, nonce]);

  async function cerrarTurno() {
    setEstado('cerrando');
    setError(null);
    try {
      const d = await api.del(`/dispositivos/sesiones/${turno.id_turno}`);
      setCierre(d?.mensaje || `Tu turno en ${turno.terminal} quedó cerrado.`);
      setEstado('cerrado');
    } catch (e) {
      setError(leerErrorDelContrato(e, { terminal: turno.terminal }));
      setEstado('abierto');
    }
  }

  return (
    <div className="max-w-xl space-y-4" data-terminal="entrar">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-gray-900">Entrar a una terminal</h1>
        <p className="text-sm text-gray-600">
          Abre tu turno en una terminal con el QR de su pantalla: lo que registres en ella queda a tu nombre.
        </p>
      </header>
      <Ayuda id="terminal.entrar.pantalla" />

      {estado === 'sin-qr' && (
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <QrCode className="mt-0.5 h-6 w-6 shrink-0 text-gray-500" aria-hidden="true" />
            <div className="min-w-0">
              <div className="font-semibold text-gray-900">Escanea el QR de la pantalla de la terminal</div>
              <p className="text-sm text-gray-600">
                Con la cámara de tu teléfono, como cualquier QR. Se abre TEGO aquí mismo y tu turno queda abierto.
              </p>
              <Ayuda id="terminal.entrar.qr" />
            </div>
          </div>
          <div className="flex items-start gap-3 text-sm text-gray-600">
            <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
            <span>¿No trajiste teléfono? En la terminal elige tu nombre y teclea tu PIN de TEGO.</span>
          </div>
        </div>
      )}

      {estado === 'abriendo' && (
        <div className="space-y-2">
          <p role="status" className="text-sm font-semibold text-gray-800">Abriendo tu turno…</p>
          <Cargando filas={2} etiqueta="Abriendo tu turno" />
        </div>
      )}

      {estado === 'error' && (
        <div className="space-y-3">
          <ErrorDelContrato error={error} />
          <p className="text-sm text-gray-600">
            Al escanear otra vez, el navegador vuelve a abrir esta pantalla con el QR nuevo.
          </p>
          {irA && (
            <button type="button" onClick={() => irA('mi-trabajo')} className={SECUNDARIO}>Ir a Mi trabajo</button>
          )}
        </div>
      )}

      {(estado === 'abierto' || estado === 'cerrando') && turno && (
        <div className="space-y-3 rounded-xl border-2 border-green-300 bg-green-50 p-4">
          <div role="status" className="flex items-start gap-2 text-sm text-green-900">
            <Check className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0 break-words">
              <strong className="block">Tu turno en {turno.terminal} está abierto.</strong>
              <span>Lo que registres queda a tu nombre{turno.operador ? ` (${turno.operador})` : ''}.</span>
            </div>
          </div>
          <ErrorDelContrato error={error} />
          <button type="button" onClick={cerrarTurno} disabled={estado === 'cerrando'} className={PRIMARIO}>
            <LogOut className="mr-1 inline h-4 w-4" aria-hidden="true" />
            {estado === 'cerrando' ? 'Cerrando…' : 'Cerrar turno'}
          </button>
          <p className="text-xs text-gray-600">
            Si no lo cierras, se cierra solo a las 8 horas o cuando otra persona entre en esa terminal.
          </p>
        </div>
      )}

      {estado === 'cerrado' && (
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <p role="status" className="text-sm font-semibold text-gray-900">{cierre}</p>
          <p className="text-sm text-gray-600">Para volver a entrar, escanea otra vez el QR de la terminal.</p>
          {irA && (
            <button type="button" onClick={() => irA('mi-trabajo')} className={SECUNDARIO}>Ir a Mi trabajo</button>
          )}
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900">Cómo funciona el turno</h2>
        <Ayuda id="terminal.turno.ficha" />
      </section>
    </div>
  );
}
